// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  Radio,
  FormLabel,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import { get, startCase, lowerCase } from 'lodash';

import {
  PROXY_NAME_TITLE,
  UNIQUE_PROXY_NAME_WARNING_MSG,
  MAXLENGTH_PROXY_NAME_WARNING_MSG,
  API_DEPLOYMENT_TYPE_TITLE,
  API_DEPLOYMENT_TYPE_TOOLTIP_TEXT,
  KEY_DEPLOYMENT_TYPE_TITLE,
  KEY_DEPLOYMENT_TYPE_TOOLTIP_TEXT,
  DETAILS_TAB_TITLE,
  KEY_STORE_AUTOMATIC_PROXY,
  KEY_STORE_EXISTING_KEY_STORE,
  KEY_STORE_SELECT_KEY,
  KEY_STORE_SELECT_TITLE,
  KEY_DEPLOYMENT_TITLE, KEY_STORE_TYPE_TITLE,
} from '../labels';
import { deleteProxyFromList, validateProxyUniqueName } from '../../../../actions/apiProxy';
import { getApiProxyDeleteError, getApiProxyDeleteStatus } from '../../../../reducers/apiProxy';
import { AlertMessages, FormAutoCompleteCombo, FormTextField, FormRadioGroup, FormDialog } from '../../../../components';
import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  FAIL,
  KEY_STORE_TYPE_OTK,
  SUCCESS,
} from '../../../../constants';

const API_PROXY_TYPE_ITEMS = [
  {
    id: 'AUTOMATIC',
    name: 'Automatic',
  },
  {
    id: 'ON_DEMAND',
    name: 'On Demand',
  },
  {
    id: 'MANUAL',
    name: 'Scripted',
  },
];

const API_KEY_PROXY_TYPE_ITEMS = [
  {
    id: 'AUTOMATIC',
    name: 'Automatic Key Deployment',
  },
  {
    id: 'ON_DEMAND',
    name: 'On Demand Key Deployment',
  },
];
const containsError = (errors, key) => errors.filter((e) => e.key === key).length > 0;
const containsErrorByType = (errors, key, type) =>
  errors.filter((e) => e.key === key && e.type === type).length > 0;

const ProxyName = ({
  classes,
  config,
  dispatch,
  currentProxyName,
  setCurrentProxyName,
  proxyDetails,
  errors,
}) => (
  <div id="proxy-name-container" className={classes.fieldContainer}>
    <FormTextField
      id={'proxy-name'}
      name={PROXY_NAME_TITLE}
      value={currentProxyName}
      error={containsError(errors, 'proxyName')}
      handleChange={
        setCurrentProxyName
      }
      handleBlur={
        () => validateProxyUniqueName({
          config,
          dispatch,
          proxyDetails,
          proxyName: currentProxyName,
        })
      }
      helperText={
        containsErrorByType(errors, 'proxyName', 'unique')
          ? UNIQUE_PROXY_NAME_WARNING_MSG
          : MAXLENGTH_PROXY_NAME_WARNING_MSG
      }
      maxLength="255"
    />
  </div>
);

ProxyName.propTypes = {
  classes: PropTypes.object,
  config: PropTypes.object,
  currentProxyName: PropTypes.string,
  setCurrentProxyName: PropTypes.func,
  proxyDetails: PropTypes.object,
  errors: PropTypes.arrayOf(PropTypes.object),
  dispatch: PropTypes.func,
};

const APIProxyTypes = ({
  classes,
  items,
  currentApiProxyType,
  setCurrentApiProxyType,
}) => (
  <div id="api-deployment-type-container" className={classes.fieldContainer}>
    <FormRadioGroup
      id="api-spec-authentication-type"
      name={API_DEPLOYMENT_TYPE_TITLE}
      tooltip={API_DEPLOYMENT_TYPE_TOOLTIP_TEXT}
      selected={currentApiProxyType}
      onChange={setCurrentApiProxyType}
      data={items}
    />
  </div>
);

APIProxyTypes.propTypes = {
  classes: PropTypes.object,
  currentApiProxyType: PropTypes.string,
  setCurrentApiProxyType: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
};

const APIKeyProxyTypes = ({
  classes,
  items,
  currentApiKeyProxyType,
  setCurrentApiKeyProxyType,
  setCurrentKeyStoreUuid,
  keyStoreList,
  proxyUuid,
  proxyDetails,
}) => {
  const [keyStoreType, setKeyStoreType] = useState('automaticKeyStore');
  const [keyStores, setKeyStores] = useState([]);
  const [selectedKeyStore, setSelectedKeyStore] = useState('');

  useEffect(() => {
    if(keyStoreList.length > 0 ){
      keyStoreList.map(item => {
        if(item.uuid === get(proxyDetails, 'keyStoreUuid')) {
          setSelectedKeyStore(item);
        }
        let keyStoreType = item.keyStoreType;
        if (item.keyStoreType !== KEY_STORE_TYPE_OTK) {
          keyStoreType = startCase(lowerCase(item.keyStoreType));
        }
        item.secondaryText = `${KEY_STORE_TYPE_TITLE}: ${keyStoreType}; ${KEY_DEPLOYMENT_TITLE}: ${startCase(lowerCase(item.deploymentType))}`;
      });
      setKeyStores(keyStoreList);
    }
  }, [keyStoreList, proxyDetails]);

  useEffect(() => {
    if(keyStoreType === 'automaticKeyStore') {
      setCurrentKeyStoreUuid('');
    } else if(!proxyUuid && keyStoreList.length > 0) {
      setSelectedKeyStore(keyStoreList[0]);
      setCurrentKeyStoreUuid(keyStoreList[0].uuid);
    }
  }, [keyStoreType]);

  const handleKeyStore = (value) => {
    if(value) {
      setSelectedKeyStore(value);
      setCurrentKeyStoreUuid(value.uuid);
      setCurrentApiKeyProxyType(value.deploymentType);
    }
  }

  return (
    <div className={classes.keyDeploymentContainer}>
      <div className={classes.keyContainer}>
        <FormLabel
          component="h3"
          className={classes.formLabel}
        >
          {!proxyUuid ? KEY_DEPLOYMENT_TYPE_TITLE : KEY_STORE_SELECT_TITLE}
        </FormLabel>
      </div>
      {!proxyUuid &&
        <div
          id="apikey-deployment-type-container"
          data-apim-test="apikey-deployment-type-container"
          className={classes.fieldContainer}
        >
          <RadioGroup value={keyStoreType}
            onChange={(e) => { setKeyStoreType(get(e, 'target.value')); }}>
            <FormControlLabel
              value='automaticKeyStore'
              control={<Radio classes={{ root: classes.radio }} />}
              label={KEY_STORE_AUTOMATIC_PROXY}
            />
              {keyStoreType === 'automaticKeyStore' &&
                <div className={classes.keyInputContainer}>
                  <FormRadioGroup
                    id="proxy-key-store-select-deployment"
                    name={''}
                    tooltip={KEY_DEPLOYMENT_TYPE_TOOLTIP_TEXT}
                    selected={currentApiKeyProxyType}
                    onChange={setCurrentApiKeyProxyType}
                    data={items}
                  />
                </div>
              }
            <FormControlLabel
              value='existingKeyStore'
              control={<Radio classes={{ root: classes.radio }} />}
              label={KEY_STORE_EXISTING_KEY_STORE}
            />
          </RadioGroup>
        </div>
      }
      {(keyStoreType === 'existingKeyStore' || proxyUuid) &&
        <div className={!proxyUuid ? classes.keyInputContainer : ''}>
          <FormAutoCompleteCombo
            data={keyStores}
            selectedValues={selectedKeyStore}
            onChange={(e, value) => handleKeyStore(value)}
            isSecondaryText={true}
            id={'proxy-key-store-select-existing-key'}
            data-apim-test={'proxy-key-store-select-existing-key'}
            inputLabel={KEY_STORE_SELECT_KEY}
          />
        </div>
      }
    </div>
  );
}

APIKeyProxyTypes.propTypes = {
  classes: PropTypes.object,
  currentApiKeyProxyType: PropTypes.string,
  setCurrentApiKeyProxyType: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  proxyUuid: PropTypes.string,
  currentKeyStoreUuid: PropTypes.string,
  setCurrentKeyStoreUuid:  PropTypes.func,
  keyStoreList: PropTypes.arrayOf(PropTypes.object),
  proxyDetails: PropTypes.object,
};

export default function Details(props) {
  const {
    classes,
    config,
    dispatch,
    proxyNameUniqueStatus,
    currentProxyName,
    setCurrentProxyName,
    currentApiProxyType,
    setCurrentApiProxyType,
    currentApiKeyProxyType,
    setCurrentApiKeyProxyType,
    proxyDetails,
    errors,
    proxyUuid,
    isEditJourney,
  } = props;

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const proxyDeletedStatus = useSelector((state) => getApiProxyDeleteStatus(state));
  const proxyDeleteError = useSelector((state) => getApiProxyDeleteError(state));
  const history = useHistory();
  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const handleDeleteProxy = () => {
    dispatch(deleteProxyFromList(proxyUuid));
    setIsDeletePending(true);
  }

  const clearNotifications = () => notifyMessages('', '');

  useEffect(() => {
    if (isDeletePending) {
      if (proxyDeletedStatus === SUCCESS) {
        setIsDeletePending(false);
        setIsOpenModal(false);
        notifyMessages(
          ALERT_SUCCESS,
          intl.getI18nMessage("label.proxy.list.delete.success.message"),
        )
        setTimeout(() => {
          history.push(`/admin/api-proxy`);
          clearNotifications();
        }, 2500);
      } else if (proxyDeletedStatus === FAIL) {
        setIsDeletePending(false);
        setIsOpenModal(false);
        notifyMessages(
          ALERT_ERROR,
          `${intl.getI18nMessage("label.proxy.list.delete.error.message")} ${proxyDeleteError}`,
        )
      }
    }
  }, [proxyDeletedStatus]);

  return (
    <Fragment>
      {notificationMessage &&
        <AlertMessages
          id="proxy-details-notification"
          variant={notificationStatus}
          message={notificationMessage}
          onClose={clearNotifications}
        />
      }
      <Box className={classes.flex}>
        <h1 className={classes.pageTitle}>{DETAILS_TAB_TITLE}</h1>
        {
          isEditJourney && isEditJourney(props) &&
          <Button
            id='delete-proxy-button'
            data-apim-test="delete-proxy-button"
            variant='outlined'
            className={classes.deleteButton}
            onClick={() => setIsOpenModal(true)}
          >
            {getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.delete.button')}
          </Button>
        }
      </Box>
      <FormDialog
        isOpen={isOpenModal}
        title={intl.getI18nMessage("label.proxy.list.delete.modal.title")}
        cancelText={intl.getI18nMessage("label.cancel.button")}
        submitText={intl.getI18nMessage("label.ok.button")}
        onCancel={() => setIsOpenModal(false)}
        onSubmit={() => handleDeleteProxy()}
      >
        {intl.getI18nMessage("label.proxy.list.delete.modal.description")}
      </FormDialog>
      <Divider />
      <ProxyName
        classes={classes}
        config={config}
        dispatch={dispatch}
        proxyNameUniqueStatus={proxyNameUniqueStatus}
        currentProxyName={currentProxyName}
        setCurrentProxyName={setCurrentProxyName}
        proxyDetails={proxyDetails}
        errors={errors}
      />
      <APIProxyTypes
        classes={classes}
        currentApiProxyType={currentApiProxyType}
        setCurrentApiProxyType={setCurrentApiProxyType}
        items={API_PROXY_TYPE_ITEMS}
      />
      <APIKeyProxyTypes
       {...props}
        classes={classes}
        currentApiKeyProxyType={currentApiKeyProxyType}
        setCurrentApiKeyProxyType={setCurrentApiKeyProxyType}
        items={API_KEY_PROXY_TYPE_ITEMS}
      />
    </Fragment>
  );
}

Details.propTypes = {
  classes: PropTypes.object,
  config: PropTypes.object,
  currentProxyName: PropTypes.string,
  setCurrentProxyName: PropTypes.func,
  proxyNameUniqueStatus: PropTypes.string,
  currentApiProxyType: PropTypes.string,
  setCurrentApiProxyType: PropTypes.func,
  currentApiKeyProxyType: PropTypes.string,
  setCurrentApiKeyProxyType: PropTypes.func,
  proxyDetails: PropTypes.object,
  errors: PropTypes.arrayOf(PropTypes.object),
  dispatch: PropTypes.func,
  deleteProxyFromList: PropTypes.func,
  proxyUuid: PropTypes.string,
  allApiProxies: PropTypes.array,
  isEditJourney: PropTypes.func,
};
